// Slack-style display forms for a URL (#12010).
//
// A link shows its whole address (minus the scheme and `www.`) whenever that
// fits its column. When it does not, the link falls back through the forms
// Slack uses, most complete first: a long query or fragment goes, then the
// middle of the path is elided with the domain, the leading segments and a
// readable last segment kept, or an opaque last segment (a share token, a
// record id) is dropped outright:
//   github.com/orgs/finpilot-ai/…/2
//   paraform.com/client/…/interviewing
//   app.box.com/s/…
// This module only lists the forms; which one fits is the renderer's call.
// The `href` stays the original URL; only the visible text changes.

const ELLIPSIS = "…";

// A final segment this long, with a digit in it and no file extension, is
// an identifier rather than a name: every token and id Slack was seen to
// drop matches, and every kept segment (`interviewing`, `AllItems.aspx`,
// `q2-2026-letter.pdf`, `11635`, `2`) does not.
const OPAQUE_MIN_LENGTH = 13;
const FILE_EXTENSION = /\.[a-z0-9]{2,5}$/i;
const isOpaque = (segment: string): boolean =>
  segment.length >= OPAQUE_MIN_LENGTH &&
  /\d/.test(segment) &&
  !FILE_EXTENSION.test(segment);

export interface UrlDisplay {
  /** What to show in place of the URL. */
  text: string;
  /** Whether `text` leaves part of the address out. Dropping the scheme and
   *  `www.` loses nothing a reader needs, so it does not count; a dropped
   *  token, a dropped query or an elided path does, and earns a tooltip
   *  with the full URL. */
  elided: boolean;
}

// `new URL` percent-encodes the path, which would turn a non-ASCII address
// (a Cyrillic wiki page, a "Shared Documents" library) into escapes the
// reader never typed. Show the characters when they decode. This has to be
// decodeURI, not decodeURIComponent: it leaves reserved characters such as
// an escaped slash (`%2F`) encoded, which is what lets the caller split the
// decoded path on `/` and still get one segment per path segment.
const readablePath = (pathname: string): string => {
  try {
    return decodeURI(pathname);
  } catch {
    return pathname;
  }
};

/**
 * The forms a URL can be shown as, most complete first: the whole address,
 * then each shorter Slack-style form in turn, none longer than the one
 * before it. Anything that does not parse as an absolute http(s) URL yields
 * the input itself as the only form.
 */
export const urlDisplayCandidates = (url: string): UrlDisplay[] => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return [{ text: url, elided: false }];
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return [{ text: url, elided: false }];
  }

  const host = parsed.host.replace(/^www\./, "");
  const segments = readablePath(parsed.pathname)
    .split("/")
    .filter((s) => s !== "");
  const tail = `${parsed.search}${parsed.hash}`;
  const path = (parts: readonly string[]): string =>
    parts.length === 0 ? host : `${host}/${parts.join("/")}`;

  // A query on a bare origin keeps its slash (`example.com/?q=1`): without
  // it the host and the query run together.
  const whole =
    tail === ""
      ? path(segments)
      : segments.length === 0
        ? `${host}/${tail}`
        : path(segments) + tail;
  const forms: UrlDisplay[] = [{ text: whole, elided: false }];
  const shorter = (text: string) => forms.push({ text, elided: true });

  if (segments.length === 0) return forms;
  const last = segments[segments.length - 1];
  const lead = segments.slice(0, -1);

  // An opaque final segment is only dropped when another segment remains, so
  // the text never collapses to the bare domain, which would make two links
  // on one card read alike. Once dropped, its ellipsis also stands for the
  // query and for anything elided before it, so no form carries two.
  if (lead.length > 0 && isOpaque(last)) {
    for (let keep = lead.length; keep >= 0; keep -= 1) {
      shorter(path([...lead.slice(0, keep), ELLIPSIS]));
    }
    return forms;
  }

  // The query string and fragment go first: they are the least readable part
  // of an address (a SharePoint `?id=…`, a signed token). The trailing
  // ellipsis says something was left out.
  if (tail !== "") shorter(path(segments) + ELLIPSIS);
  // Then the middle of the path, one leading segment at a time, always
  // keeping the readable last one.
  for (let keep = lead.length - 1; keep >= 0; keep -= 1) {
    shorter(path([...lead.slice(0, keep), ELLIPSIS, last]));
  }
  return forms;
};
